import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'components/Link';
import ShopButtonInfo from 'components/ShopButtonInfo';
import trimText from 'lib/trimText';
import buildAffiliateLink from 'lib/buildAffiliateLink';
import {
  product as productPropType,
} from 'lib/CustomPropTypes';

import {
  WaffleTeaseImage,
  WaffleHeadline,
  WaffleTextSummary,
  WaffleToggleButton,
} from 'components/packages/Waffle/Shared';
import CardOverlay from 'components/CardOverlay';

import { getDek, getInfoForShopButtons, getPrimaryOfferPrices } from 'lib/teaseUtils';
import styles from './ProductWaffleCardStyles.scss';

const block = 'ProductWaffleCard';

class ProductWaffleCard extends React.Component {
  static propTypes = {
    card: productPropType.isRequired,
    vertical: PropTypes.string,
  };

  static contextTypes = {
    canonicalUrl: PropTypes.string,
  };

  static defaultProps = {
    vertical: 'today',
  };

  constructor(props) {
    super(props);
    this.state = {
      activateShowMore: false,
    };
  }

  updateShowMore = (currentState) => {
    this.setState({ activateShowMore: currentState });
  }

  isDescriptionMoreThan80Chars = (description) => description && description.length > 80;

  renderShopButtons() {
    const {
      productUrl, sale, list, productName, seller,
    } = getInfoForShopButtons(this.props);

    return (
      <ShopButtonInfo
        productUrl={productUrl || ''}
        seller={seller.name || ''}
        name={productName || ''}
        listPrice={list}
        salePrice={sale}
        additionalStyles={{
          shop: classNames(styles.shop),
          vendor: classNames(styles.vendor, 'pv0'),
          cost: classNames(styles.cost, 'f5 f3-m f4-l ph3'),
          vendorName: classNames(styles.vendorName, 'tl'),
        }}
        block="WaffleProduct"
        isLink
      />
    );
  }

  renderOverlayContent() {
    const { card, vertical } = this.props;
    const { showMoreInfo } = this.state;
    const { name: productName, offers, productDescription } = card.item || card || {};
    const { sale, list } = getPrimaryOfferPrices(card);
    const { externalUrl, seller } = (offers && offers.length && offers[0]) || {};
    const productUrl = (externalUrl || (seller && seller.externalUrl)) || {};
    const link = buildAffiliateLink(productUrl, null);
    const { html: prodDescription } = (productDescription
      && productDescription.length && productDescription[0]) || {};

    const dek = getDek(this.props);

    return (
      <section
        className={classNames(
          styles.productInfo,
          'waffle-card__scrollable-more-info',
          'ph5 ph7-xl pb2 pt5 df flex-column flex-grow-1 bg-white',
        )}
      >
        <div className="mb6">
          {this.renderShopButtons()}
        </div>
        <WaffleHeadline
          url={link}
          headline={productName}
          vertical={vertical}
          isTwoUpMobile={false}
        />
        <div className={classNames(styles.productPrice, styles.isOverLay, 'founders-mono f4 mt4 fw5 mb3 dn-m')}>
          {sale && (
          <div className="vermillion b mb1">
            $
            {sale}
          </div>
          )}
          <div className={classNames('gray-60 b', { 'strike fw5': sale })}>
            $
            {list}
          </div>
        </div>
        <WaffleTextSummary
          showDek
          showMoreInfo={showMoreInfo}
          dekText={dek || prodDescription}
          allowHTMLText
        />
      </section>
    );
  }

  render() {
    const { activateShowMore } = this.state;
    const { card, card: { computedValues = {} }, vertical } = this.props;
    if (!card.id) return null; // empty card
    const {
      name: productName,
      offers,
      productDescription,
      id,
      teaseImage,
      isCustomAd,
    } = card.item || card;

    const dek = getDek(this.props);

    const { externalUrl, seller } = (offers && offers.length && offers[0]) || {};
    const { sale, list } = getPrimaryOfferPrices(card);
    const { html: prodDescription } = (productDescription
      && productDescription.length && productDescription[0]) || {};
    const productUrl = (externalUrl || (seller && seller.externalUrl)) || {};
    const hasMoreInfo = this.isDescriptionMoreThan80Chars(dek || prodDescription);
    const link = buildAffiliateLink(productUrl, null);

    return !isCustomAd && (
      <li
        key={id}
        className={classNames(
          block,
          'waffle-card',
          styles.productWaffleCard,
          'bg-white',
          {
            'plum publico-hed': vertical === 'today',
          },
        )}
      >
        <div className="test__product-waffle-card h-100 df flex-column">
          <Link
            to={link}
            target="_blank"
            rel="noopener noreferrer"
            className={classNames('db relative')}
          >
            <WaffleTeaseImage
              url={link}
              vertical={vertical}
              computedValues={
                // Vilynx based waffles don't return computedValues, so build if they don't exist
                computedValues.alternateTeaseImages ? computedValues : {
                  alternateTeaseImages: [],
                  teaseImage,
                }
              }
              showTease
            />
          </Link>
          <section
            className={classNames(
              'ph5 pt5 pt7-xl ph7-xl df flex-column flex-grow-1',
              { pb9: hasMoreInfo, pb7: !hasMoreInfo },
            )}
          >
            <div className="mb4 mb6-m mb5-l">
              {this.renderShopButtons()}
            </div>
            <WaffleHeadline
              className="mb0"
              url={link}
              headline={trimText(productName, 80)}
              vertical={vertical}
              isTwoUpMobile
            />
            <div className={classNames(styles.productPrice, 'founders-mono f4 fw5 mt3 mb3 dn-m')}>
              {sale && (
              <div className="vermillion b mb1">
                $
                {sale}
              </div>
              )}
              <div className={classNames('gray-60 b', { 'strike fw5': sale })}>
                $
                {list}
              </div>
            </div>
            <WaffleTextSummary
              showDek
              showMoreInfo={false}
              dekText={dek || prodDescription}
              trimDekText
              allowHTMLText
              textClasses="dn db-m"
            />
          </section>

          {hasMoreInfo
            && (
              <React.Fragment>
                <CardOverlay isActive={activateShowMore} useModal>
                  {this.renderOverlayContent()}
                  {/* This is inside the overlay */}
                  <WaffleToggleButton
                    className="w-100 ph5 ph7-xl fixed bottom0 left0 right0 z-1 dn-m"
                    activeText="Less"
                    inactiveText="More"
                    onChangeCallback={this.updateShowMore}
                    isActive={activateShowMore}
                  />
                </CardOverlay>

                {/* This is on the card */}
                <WaffleToggleButton
                  className="w-100 ph5 ph7-xl absolute bottom0 left0 right0 z-1"
                  activeText="Less"
                  inactiveText="More"
                  onChangeCallback={this.updateShowMore}
                  isActive={activateShowMore}
                />
              </React.Fragment>
            )}
        </div>
      </li>
    );
  }
}

export default ProductWaffleCard;
